import { createApi } from "@reduxjs/toolkit/query/react";
import customFetchBase from "./customFetchBase";
import { getQueryParams } from "utils/getQueryParams";
import {
  IGenericResponse,
  IPaginatedList,
  IPaginationQuery,
} from "Interfaces/PaginatedList";
import { IOperation } from "Models/Operation";
import { OperationFilters } from "Interfaces/OperationFilters";

export const operationApi = createApi({
    reducerPath: "operationApi",
    baseQuery: customFetchBase,
    tagTypes: ["Operation"],
    keepUnusedDataFor: 300,  // Keep cached data for 5 minutes globally
    refetchOnReconnect: true, // Refetch data when reconnected to the network
    refetchOnFocus: true, // Refetch data when the window is focused again
    endpoints: (builder) => ({
        getOperation: builder.query<IPaginatedList<IOperation>, IPaginationQuery<OperationFilters>>({
            query(queryFilters) {
                return {
                    url: getQueryParams(`operations`, queryFilters),
                    credentials: "include",
                };
            },
            transformResponse: (results: IGenericResponse<IPaginatedList<IOperation>>) =>
                results.data,
            providesTags: ["Operation"],
        }),
        // Get single operation
        getAPieceOfOperation: builder.query<IOperation, string>({
            query(operationId) {
                return {
                    url: "operations/" + operationId,
                    method: "GET",
                    credentials: "include",
                };
            },
            providesTags: ["Operation"],
            transformResponse: (result: { data: IOperation }) => {
                return result.data;
            },
        }),
        //GET operation recomendation
        getOperationRecomendation: builder.query<IOperation, string>({
            query(operationId) {
                return {
                    url:`operations/recomendation/${operationId}`,
                    method: "GET",
                    credentials: "include",
                };
            },
            providesTags: ["Operation"],
            transformResponse: (result: { data: IOperation }) => {
                return result.data;
            },
        }),
        //GET all operations from a company
        getOperationByCompany: builder.query<IOperation[], string>({
            query(companyId) {
                return {
                    url:`operations/company/${companyId}`,
                    method: "GET",
                    credentials: "include",
                };
            },
            providesTags: ["Operation"],
            transformResponse: (results: IGenericResponse<IOperation[]>) =>
                results.data,
        }),
        //GET all operations from a point of sale
        getOperationByPointOfSale: builder.query<IOperation[], string>({
            query(pointOfSaleId) {
                return {
                    url:`operations/point-of-sale/${pointOfSaleId}`,
                    method: "GET",
                    credentials: "include",
                };
            },
            providesTags: ["Operation"],
            transformResponse: (results: IGenericResponse<IOperation[]>) =>
                results.data,
        }),
        // Post operation
        postOperation: builder.mutation<{ data: IOperation; status: string } | IOperation, FormData>({
            query(newOperation) {
                return {
                    url: "operations",
                    method: "POST",
                    body: newOperation,
                    credentials: "include",
                };
            },
            invalidatesTags: ["Operation"],
        }),
        // Put operation
        putOperation: builder.mutation<{ data: IOperation; status: string } | IOperation, {id:string, operationToUpdate: FormData}>({
            query({id: operationId, operationToUpdate:updatedOperation}) {
                return {
                    url: "operations/" + operationId,
                    method: "PUT",
                    body: updatedOperation,
                    credentials: "include",
                };
            },
            invalidatesTags: ["Operation"],
        }),
        // Post bill operation
        postBillOperation: builder.mutation<IOperation, {id:string, operationToUpdate: FormData}>({
            query({id: operationId, operationToUpdate:updatedOperation}) {
                return {
                    url: "operations/bill/" + operationId,
                    method: "POST",
                    body: updatedOperation,
                    credentials: "include",
                };
            },
            invalidatesTags: ["Operation"],
        }),
        // Post pin operation
        postEmailOperation: builder.mutation<IOperation, {id:string, operationToUpdate: FormData}>({
            query({id: operationId, operationToUpdate:updatedOperation}) {
                return {
                    url: "operations/email/" + operationId,
                    method: "POST",
                    body: updatedOperation,
                    credentials: "include",
                };
            },
            invalidatesTags: ["Operation"],
        }),

        //verify pin operation
        postCheckPinOperation: builder.mutation<IOperation, {id:string, operationToUpdate: FormData}>({
            query({id: operationId, operationToUpdate:updatedOperation}) {
                return {
                    url: "operations/check-pin/" + operationId,
                    method: "POST",
                    body: updatedOperation,
                    credentials: "include",
                };
            },
            invalidatesTags: ["Operation"],
        }),
        // Delete operation
        deleteOperation: builder.mutation<IOperation, string>({
            query(operationId) {
                return {
                    url: "operations/" + operationId,
                    method: "DELETE",
                    credentials: "include",
                };
            },
            invalidatesTags: ["Operation"],
        }),

    }),
});

export const {
    useGetOperationQuery,
    useGetOperationByCompanyQuery,
    useGetOperationByPointOfSaleQuery,
    useGetAPieceOfOperationQuery,    
    usePostOperationMutation,
    usePutOperationMutation,
    usePostBillOperationMutation,
    usePostEmailOperationMutation,
    usePostCheckPinOperationMutation,
    useDeleteOperationMutation,
    useGetOperationRecomendationQuery
} = operationApi;